import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  HeroCard,
  Card,
  TipBox,
  PrimaryButton,
  SecondaryButton
} from '../shared';
import InviteFamilyModal from '../../familytree/InviteFamilyModal';
import { API_CONFIG } from '../../../config/api';
import { getUser } from '../../../utils/secureStorage';

// Helper to get full URL for profile photos
const getFullPhotoUrl = (relativeUrl) => {
  if (!relativeUrl) return null;
  if (relativeUrl.startsWith('http')) return relativeUrl;
  return `${API_CONFIG.BASE_URL}${relativeUrl}`;
};

/**
 * FamilyMemberCard Component
 *
 * Card display for a family member with profile photo
 */
function FamilyMemberCard({ member, onClick }) {
  const isMinor = member.isMinor;

  return (
    <div
      onClick={onClick}
      className={`rounded-2xl p-4 border-2 flex items-center gap-4 cursor-pointer transition-all hover:shadow-lg ${
        isMinor
          ? 'bg-blue-50 border-blue-200 hover:border-blue-400'
          : 'bg-white border-gray-200 hover:border-green-500'
      }`}
    >
      {/* Avatar */}
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl relative flex-shrink-0 overflow-hidden"
        style={{ background: member.profilePhotoUrl ? 'transparent' : member.avatarColor }}
      >
        {member.profilePhotoUrl ? (
          <img
            src={getFullPhotoUrl(member.profilePhotoUrl)}
            alt={member.firstName}
            className="w-full h-full object-cover"
          />
        ) : (
          member.firstName?.charAt(0).toUpperCase()
        )}

        {/* Online Dot */}
        {member.isOnline && (
          <span
            className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white"
            style={{ background: '#22C55E' }}
          />
        )}

        {/* Minor Badge */}
        {isMinor && (
          <span
            className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-xs"
            style={{ background: '#0EA5E9' }}
          >
            👶
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="font-bold text-gray-900 truncate text-lg">
          {member.firstName} {member.lastName || ''}
        </div>
        <div className="text-sm text-gray-600">
          {member.relationship || (isMinor ? 'Child' : 'Family member')}
        </div>
        {isMinor && member.guardianName && (
          <div className="text-xs text-blue-600">
            via {member.guardianName}
          </div>
        )}
      </div>

      {/* Arrow */}
      <span className="text-gray-400 text-xl">›</span>
    </div>
  );
}

/**
 * AudienceReadyState Component
 *
 * Dashboard state for users with family connected but no content yet
 * Goal: Get them to share their first story
 */
function AudienceReadyState({ user, connections, questions }) {
  const navigate = useNavigate();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Get familyId from localStorage
  const getFamilyId = () => {
    const familyId = localStorage.getItem('familyId');
    if (familyId) return familyId;

    const userData = getUser() || {};
    return userData.family_id || userData.familyId || null;
  };

  // Get personId from localStorage
  const getPersonId = () => {
    const userData = getUser() || {};
    return userData.person_id || userData.personId || null;
  };

  const familyId = getFamilyId();
  const personId = getPersonId();

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  const handleCloseInviteModal = (memberWasAdded = false) => {
    setIsInviteModalOpen(false);

    // If a member was added, optionally refresh the page or refetch data
    if (memberWasAdded) {
      // Refresh the page to show updated connection count
      window.location.reload();
    }
  };

  const connectionNames = connections
    .slice(0, 3)
    .map(c => c.firstName)
    .join(', ');

  // Determine card order: if more than 2 connections, show "Share Story" first
  const showStoryFirst = connections.length > 2;

  // Separate adults and minors for display
  const adults = connections.filter(c => !c.isMinor);
  const minors = connections.filter(c => c.isMinor);

  // Your Family Card
  const FamilyCard = (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-3xl">👨‍👩‍👧‍👦</span>
          <h2 className="text-2xl font-bold text-gray-800">Your Family</h2>
        </div>
        <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-bold">
          {connections.length} Connected
        </div>
      </div>

      {/* Adults Grid */}
      {adults.length > 0 && (
        <div className="mb-4">
          <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Adults ({adults.length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {adults.slice(0, 6).map((member) => (
              <FamilyMemberCard
                key={member.id}
                member={member}
                onClick={() => navigate(`/family-hub/member/${member.id}`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Children Grid */}
      {minors.length > 0 && (
        <div className="mb-4">
          <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Children ({minors.length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {minors.slice(0, 6).map((member) => (
              <FamilyMemberCard
                key={member.id}
                member={member}
                onClick={() => navigate(`/family-hub/member/${member.id}`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Show more link if needed */}
      {connections.length > 6 && (
        <button
          onClick={() => navigate('/family-hub?tab=members')}
          className="text-green-600 font-semibold hover:underline mb-4 block"
        >
          View all {connections.length} family members →
        </button>
      )}

      <SecondaryButton
        icon="➕"
        onClick={handleOpenInviteModal}
        centered
      >
        Invite More Family
      </SecondaryButton>
    </Card>
  );

  // Share Your First Story Card
  const StoryCard = (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-3xl">🎙️</span>
        <h2 className="text-2xl font-bold text-gray-800">
          Share Your First Story
        </h2>
      </div>

      <p className="text-lg text-gray-700 mb-5 leading-relaxed">
        Your family is ready and waiting! Answer a question or share a memory to get started.
      </p>

      <PrimaryButton
        icon="🎙️"
        onClick={() => navigate('/tell-a-story')}
        className="mb-4"
      >
        Record Your First Story
      </PrimaryButton>

      <div className="grid grid-cols-2 gap-3">
        <SecondaryButton
          icon="📷"
          onClick={() => navigate('/photos')}
        >
          Share a Photo
        </SecondaryButton>
        <SecondaryButton
          icon="📝"
          onClick={() => navigate('/tell-a-story')}
        >
          Write a Memory
        </SecondaryButton>
      </div>
    </Card>
  );

  // Questions Card (only if questions exist)
  const QuestionsCard = questions && questions.length > 0 ? (
    <Card variant="questions">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-3xl">❓</span>
          <h2 className="text-2xl font-bold text-orange-900">
            They Want to Know...
          </h2>
        </div>
        <div className="bg-orange-200 text-orange-900 px-4 py-2 rounded-full font-bold">
          {questions.length} {questions.length === 1 ? 'Question' : 'Questions'}
        </div>
      </div>

      <div className="space-y-4">
        {questions.map(question => (
          <div
            key={question.id}
            className="bg-white rounded-xl p-4 border border-orange-200"
          >
            <div className="flex items-start gap-3 mb-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0"
                style={{ backgroundColor: question.askedBy.avatarColor }}
              >
                {question.askedBy.firstName[0]}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">
                  {question.askedBy.firstName}
                  {question.isFromMinor && question.viaParent && (
                    <span className="text-sm text-blue-600 ml-2">
                      (via {question.viaParent})
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  {question.askedBy.relationship}
                </div>
              </div>
            </div>
            <p className="text-lg text-gray-800 mb-3 italic">
              "{question.text}"
            </p>
            <button
              onClick={() => navigate(`/questions/${question.id}/answer`)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-bold transition-colors"
            >
              Answer This Question
            </button>
          </div>
        ))}
      </div>
    </Card>
  ) : null;

  return (
    <div className="min-h-screen bg-[#F5F5DC] p-6">
      <div className="max-w-4xl mx-auto">
        <HeroCard
          emoji="🎉"
          title="Your Family is Connected!"
          subtitle={`${connections.length} ${connections.length === 1 ? 'person is' : 'people are'} waiting to hear from you`}
          variant="success"
        />

        {/* Card order depends on connection count */}
        {showStoryFirst ? (
          <>
            {StoryCard}
            {QuestionsCard}
            {FamilyCard}
          </>
        ) : (
          <>
            {FamilyCard}
            {QuestionsCard}
            {StoryCard}
          </>
        )}

        <TipBox
          icon="✨"
          title="Your audience is ready!"
          text={`${connectionNames} will be notified as soon as you share something. They're excited to hear from you!`}
        />

        {/* Invite Family Modal */}
        <InviteFamilyModal
          isOpen={isInviteModalOpen}
          onClose={handleCloseInviteModal}
          familyId={familyId}
          rootPersonId={personId}
        />
      </div>
    </div>
  );
}

AudienceReadyState.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    avatarInitial: PropTypes.string.isRequired
  }).isRequired,
  connections: PropTypes.array.isRequired,
  questions: PropTypes.array
};

export default AudienceReadyState;
